import { ContactNoteInterface } from 'modules/crm/pipeline/contact-card/note/type/contact-note-interface'
import { useMemo, useRef } from 'react'
import { useControlledSwr } from 'shared/hooks/use-controlled-swr'
import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import useUser from 'shared/hooks/use-user'
import {
  DataWithPaginationInterface,
  OrderEnum,
  SwrPaginationInterface,
} from 'shared/types/pagination-interface'
import { getPaginationQueryString } from 'shared/utils/api/get-pagination-query-string'
import { getNotesApi } from './use-notes'

interface UseContactNotesProps extends SwrPaginationInterface {
  contactId: number | null
  isFetchAllowed: boolean
}

export type ContactNotesWithPagination = ContactNoteInterface & { paginationId: number }

export const useContactNotesOffsetPagination = ({
  contactId,
  isFetchAllowed,
  limit,
  pagination,
}: UseContactNotesProps) => {
  const { user } = useUser()
  const { fetcher } = useFetch<
    RequestMethodsEnum.get,
    DataWithPaginationInterface<ContactNoteInterface>
  >({
    method: RequestMethodsEnum.get,
  })

  const maxIndex = (() => {
    if (pagination.startFromId) {
      if (Number(pagination.startFromId) % limit !== 0) return
      return pagination.startFromId
    } else {
      return
    }
  })()

  const { data } = useControlledSwr(
    `${getNotesApi(contactId)}?${getPaginationQueryString({
      limit,
      pagination: {
        order: OrderEnum.next,
        startFromId: maxIndex,
      },
    })}`,
    fetcher,
    undefined,
    isFetchAllowed && !!user,
  )

  const fullListRef = useRef<ContactNotesWithPagination[]>([])
  const fullList: ContactNotesWithPagination[] | undefined = useMemo(() => {
    if (!data) return

    const updatedList: ContactNoteInterface[] = [...fullListRef.current]

    data.items.forEach(note => {
      if (!updatedList.find(item => item.id === note.id)) {
        updatedList.push(note)
      }
    })

    const updatedListWithPagination = updatedList.map((note, index) => ({
      ...note,
      paginationId: index + 1,
    }))

    fullListRef.current = updatedListWithPagination
    return updatedListWithPagination
  }, [data])

  const notesToShow: DataWithPaginationInterface<ContactNotesWithPagination> | undefined =
    useMemo(() => {
      if (!fullList) return

      const startFromId = pagination.startFromId ? Number(pagination.startFromId) : 0

      if (pagination.order === OrderEnum.next) {
        return {
          items: fullList.slice(startFromId, startFromId + limit),
          hasMore: fullList.length >= startFromId + limit,
        }
      } else {
        const startFromIdPrevOrder = startFromId - 1
        const past = fullList.length - startFromIdPrevOrder
        return {
          items: fullList.slice(startFromIdPrevOrder - limit, startFromIdPrevOrder),
          hasMore: fullList.length - past > limit,
        }
      }
    }, [fullList, pagination.startFromId, limit, pagination.order])

  return { data: notesToShow }
}
